import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Globe, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import Card from '../components/Card';
import PriceChange from '../components/PriceChange';
import LoadingSpinner from '../components/LoadingSpinner';
import MarketAssetRow from '../components/MarketAssetRow';
import OrderTicket from '../components/trading/OrderTicket';
import { useMarketWS } from '../hooks/useMarketWS';
import eodhd from '../services/eodhd';
import { JSE_STOCKS, US_STOCKS, LSE_STOCKS, CRYPTOCURRENCIES, FOREX_PAIRS, INDICES, COMMODITIES, ETFS } from '../utils/constants';

type MarketTab = 'jse' | 'us' | 'lse' | 'crypto' | 'forex' | 'indices' | 'commodities' | 'etfs';

interface MarketAsset {
  symbol: string;
  name: string;
  price: number | null;
  change: number;
  volume?: string;
  marketCap?: string;
  previousClose?: number;
}

interface SectorPerformance {
  name: string;
  change: number;
}

const MarketTableSkeleton: React.FC = () => (
  <div className="animate-pulse">
    {Array.from({ length: 10 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 py-3 border-b border-slate-700/30">
        <div className="w-8 h-4 bg-slate-700 rounded" />
        <div className="w-16 h-4 bg-slate-700 rounded" />
        <div className="flex-1 h-4 bg-slate-700 rounded" />
        <div className="w-20 h-4 bg-slate-700 rounded ml-auto" />
        <div className="w-16 h-4 bg-slate-700 rounded" />
        <div className="w-16 h-4 bg-slate-700 rounded" />
      </div>
    ))}
  </div>
);

const Markets: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Map dashboard tab IDs to market tab IDs
  const tabMapping: Record<string, MarketTab> = {
    stocks: 'jse',
    jse: 'jse',
    us: 'us',
    lse: 'lse',
    crypto: 'crypto',
    forex: 'forex',
    indices: 'indices',
    commodities: 'commodities',
    etfs: 'etfs',
  };

  // Initialize activeTab from URL parameter or default to 'jse'
  const initialTab = (() => {
    const typeParam = searchParams.get('type');
    return (typeParam && tabMapping[typeParam]) ? tabMapping[typeParam] : 'jse';
  })();

  const [activeTab, setActiveTab] = useState<MarketTab>(initialTab);
  const [orderTicket, setOrderTicket] = useState<{ symbol: string; price: number } | null>(null);

  // Update tab when URL params change
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam && tabMapping[typeParam]) {
      setActiveTab(tabMapping[typeParam]);
    }
  }, [searchParams]);

  const tabs: { id: MarketTab; label: string; icon: string }[] = [
    { id: 'jse', label: 'JSE', icon: '🇿🇦' },
    { id: 'us', label: 'US Markets', icon: '🇺🇸' },
    { id: 'lse', label: 'London (LSE)', icon: '🇬🇧' },
    { id: 'crypto', label: 'Crypto', icon: '₿' },
    { id: 'forex', label: 'Forex', icon: '💱' },
    { id: 'commodities', label: 'Gold & Commodities', icon: '🪙' },
    { id: 'etfs', label: 'ETFs', icon: '📦' },
    { id: 'indices', label: 'Indices', icon: '📊' },
  ];

  const sectorPerformance: SectorPerformance[] = [
    { name: 'Technology', change: 2.4 },
    { name: 'Financials', change: 1.2 },
    { name: 'Mining', change: -0.8 },
    { name: 'Energy', change: -1.5 },
    { name: 'Healthcare', change: 0.9 },
    { name: 'Consumer', change: 0.3 },
  ];

  const getAssetsForTab = (tab: MarketTab = activeTab) => {
    switch (tab) {
      case 'jse':
        return JSE_STOCKS;
      case 'us':
        return US_STOCKS;
      case 'lse':
        return LSE_STOCKS;
      case 'crypto':
        return CRYPTOCURRENCIES.map((c) => ({ symbol: c.symbol, name: c.name, sector: 'Crypto' }));
      case 'forex':
        return FOREX_PAIRS.map((f) => ({ symbol: f.symbol, name: f.name, sector: 'Forex' }));
      case 'indices':
        return INDICES.map((i) => ({ symbol: i.symbol, name: i.name, sector: 'Index' }));
      case 'commodities':
        return COMMODITIES.map((c) => ({ symbol: c.symbol, name: c.name, sector: 'Commodity' }));
      case 'etfs':
        return ETFS.map((e) => ({ symbol: e.symbol, name: e.name, sector: e.category }));
      default:
        return JSE_STOCKS;
    }
  };

  const buildQueryFn = (tab: MarketTab) => async () => {
    const sourceAssets = getAssetsForTab(tab);

    // For JSE, use optimised IRESS batch endpoint
    if (tab === 'jse') {
      const jseQuotes = await eodhd.getAllJSEQuotes();

      return sourceAssets.map((asset) => {
        const quote = jseQuotes.find(q => q.symbol === asset.symbol || q.code === asset.symbol);
        if (quote) {
          return {
            symbol: asset.symbol,
            name: asset.name,
            price: quote.close || 0,
            change: quote.change_percent ?? quote.change_p ?? 0,
            volume: quote.volume?.toLocaleString() || 'N/A',
            previousClose: quote.previousClose ?? quote.previous_close,
          };
        }
        return { symbol: asset.symbol, name: asset.name, price: null, change: 0, volume: 'N/A' };
      });
    }

    // For US and LSE, use bulk quote endpoint (single request for all stocks)
    if (tab === 'us' || tab === 'lse') {
      const symbols = sourceAssets.map(a => a.symbol);
      const bulkQuotes = await eodhd.getBulkQuotes(symbols);
      const quoteMap = new Map(bulkQuotes.map(q => [q.symbol ?? q.code, q]));

      return sourceAssets.map((asset) => {
        const quote = quoteMap.get(asset.symbol);
        if (quote) {
          return {
            symbol: asset.symbol,
            name: asset.name,
            price: quote.close || 0,
            change: quote.change_percent ?? quote.change_p ?? 0,
            volume: quote.volume?.toLocaleString() || 'N/A',
            previousClose: quote.previousClose ?? quote.previous_close,
          };
        }
        return { symbol: asset.symbol, name: asset.name, price: null, change: 0, volume: 'N/A' };
      });
    }

    // For other markets, fetch individually
    const assetPromises = sourceAssets.map(async (asset) => {
      try {
        const data = await eodhd.getRealTimePrice(asset.symbol);
        return {
          symbol: asset.symbol,
          name: asset.name,
          price: data.close || 0,
          change: data.change_percent ?? data.change_p ?? 0,
          volume: data.volume?.toLocaleString() || 'N/A',
          previousClose: data.previousClose ?? data.previous_close,
        };
      } catch {
        return {
          symbol: asset.symbol,
          name: asset.name,
          price: null,
          change: 0,
          volume: 'N/A',
        };
      }
    });

    return await Promise.all(assetPromises);
  };

  const queryOptions = (tab: MarketTab) => ({
    queryKey: ['market-data', tab] as const,
    queryFn: buildQueryFn(tab),
    staleTime: 15 * 60 * 1000, // 15 minutes — aligned with IRESS update frequency
    gcTime: 20 * 60 * 1000,    // 20 minutes cache time
    refetchOnWindowFocus: false,
  });

  // Use React Query for data fetching with automatic caching.
  // placeholderData: keepPreviousData shows the last tab's data while the new
  // tab is loading, eliminating blank flashes on tab switches.
  const { data: assets = [], isLoading, isFetching, isPlaceholderData, dataUpdatedAt, refetch } = useQuery({
    ...queryOptions(activeTab),
    placeholderData: keepPreviousData,
  });

  // Single shared WebSocket for all streaming symbols on the current tab.
  // Replaces per-row useRealtimePrice calls which opened N simultaneous connections
  // (one per MarketAssetRow), causing rapid price flashing on Crypto/Forex tabs.
  const streamingSymbols = useMemo(
    () => assets.map(a => a.symbol).filter(s => s.endsWith('.FOREX') || s.endsWith('.CC')),
    [assets]
  );
  const { ticks: liveTicks } = useMarketWS({ symbols: streamingSymbols });

  // Prefetch US and LSE only after the active tab has successfully loaded.
  // This ensures JSE (or whichever tab is active) finishes first before
  // background requests compete for bandwidth/server resources.
  useEffect(() => {
    if (isLoading) return; // active tab still loading — don't prefetch yet
    const timer = setTimeout(() => {
      const tabsToPrefetch: MarketTab[] = ['us', 'lse'];
      for (const tab of tabsToPrefetch) {
        if (tab !== activeTab) {
          queryClient.prefetchQuery(queryOptions(tab));
        }
      }
    }, 500); // small buffer so the active tab can paint before prefetch starts
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, queryClient]); // re-evaluate whenever loading state changes

  const getCurrencySymbol = (symbol?: string) => {
    // If symbol is provided, check its suffix
    if (symbol) {
      if (symbol.endsWith('.JSE')) return 'R';
      if (symbol.endsWith('.FOREX')) return '';
    }

    // Fallback to tab-based currency
    switch (activeTab) {
      case 'jse':
        return 'R';
      case 'forex':
        return '';
      case 'lse':
        return '£';
      case 'commodities':
      case 'etfs':
      case 'us':
      case 'crypto':
      case 'indices':
      default:
        return '$';
    }
  };

  const dataAge = dataUpdatedAt
    ? Math.round((Date.now() - dataUpdatedAt) / 60000)
    : null;

  const fetchedAtTime = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="text-teal-400" />
            Markets Overview
          </h1>
          <p className="text-sm text-slate-400 mt-1">Real-time market data across global exchanges</p>
        </div>
        <div className="flex items-center gap-4">
          {dataUpdatedAt > 0 && !isPlaceholderData && (
            <span className="text-xs text-slate-500">
              {fetchedAtTime}{dataAge! > 0 ? ` · ${dataAge}m ago` : ''}
            </span>
          )}
          {isPlaceholderData && (
            <span className="text-xs text-amber-400">Loading…</span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Market Tabs */}
      <div className="flex flex-wrap gap-2 p-1 bg-slate-800 rounded-lg">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-all
              ${
                activeTab === tab.id
                  ? 'bg-teal-500/20 text-teal-400'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
              }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Market Table */}
        <div className="lg:col-span-2">
          <Card title={`${tabs.find((t) => t.id === activeTab)?.label} Assets`}>
            {isLoading && !isPlaceholderData ? (
              <MarketTableSkeleton />
            ) : (
              <div className={`overflow-x-auto transition-opacity duration-200 ${isPlaceholderData ? 'opacity-60' : 'opacity-100'}`}>
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-700/50">
                      <th className="pb-3 font-medium w-12"></th>
                      <th className="pb-3 font-medium">Symbol</th>
                      <th className="pb-3 font-medium">Name</th>
                      <th className="pb-3 font-medium text-right">Price</th>
                      <th className="pb-3 font-medium text-right">Change</th>
                      <th className="pb-3 font-medium text-right">Volume</th>
                      <th className="pb-3 font-medium w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset, i) => (
                      <MarketAssetRow
                        key={i}
                        symbol={asset.symbol}
                        name={asset.name}
                        price={asset.price}
                        change={asset.change}
                        volume={asset.volume}
                        currencySymbol={getCurrencySymbol(asset.symbol)}
                        previousClose={asset.previousClose}
                        onBuy={(sym, price) => setOrderTicket({ symbol: sym, price })}
                        liveTick={liveTicks[asset.symbol]}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Market Summary */}
          <Card title="Market Summary">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Advancing</span>
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-emerald-400" />
                  <span className="text-emerald-400 font-mono">
                    {assets.filter((a) => a.change > 0).length}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Declining</span>
                <div className="flex items-center gap-2">
                  <TrendingDown size={14} className="text-red-400" />
                  <span className="text-red-400 font-mono">
                    {assets.filter((a) => a.change < 0).length}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Unchanged</span>
                <span className="text-slate-400 font-mono">
                  {assets.filter((a) => a.change === 0).length}
                </span>
              </div>
            </div>
          </Card>

          {/* Sector Performance */}
          <Card title="Sector Performance">
            <div className="space-y-3">
              {sectorPerformance.map((sector, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">{sector.name}</span>
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2 rounded-full ${sector.change >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.abs(sector.change) * 20}px` }}
                    />
                    <span
                      className={`text-sm font-mono ${
                        sector.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {sector.change >= 0 ? '+' : ''}
                      {sector.change.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Movers Quick View */}
          <Card title="Top Movers">
            <div className="space-y-2">
              {[...assets]
                .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
                .slice(0, 5)
                .map((asset, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1 cursor-pointer hover:bg-slate-700/30 px-2 -mx-2 rounded"
                    onClick={() => navigate(`/asset/${asset.symbol}`)}
                  >
                    <span className="text-sm font-mono text-teal-400">{asset.symbol.split('.')[0]}</span>
                    <PriceChange value={asset.change} size="sm" />
                  </div>
                ))}
            </div>
          </Card>
        </div>
      </div>
      <OrderTicket
        symbol={orderTicket?.symbol ?? ''}
        currentPrice={orderTicket?.price ?? 0}
        isOpen={!!orderTicket}
        onClose={() => setOrderTicket(null)}
      />
    </div>
  );
};

export default Markets;
